import { sql } from "drizzle-orm";
import {
  boolean,
  index,
  integer,
  pgTable,
  primaryKey,
  text,
  timestamp,
  uniqueIndex,
} from "drizzle-orm/pg-core";

import { endUsers, environments } from "./app.js";
import { tenant, user } from "./auth.js";

// ---- Twitch -----------------------------------------------------------------------------------
// Live breaks stream on Twitch (docs/live-breaks.md, "Twitch"). Each environment brings its
// own Twitch application, so Twitch's consent screen and a shopper's Twitch connections name the
// store, never the platform. Every secret here is AES-256-GCM under `TWITCH_TOKEN_KEY`
// (`setTwitchTokenCipher`), and no `_ct` column ever reaches a tenant-facing response.

/**
 * An environment's own Twitch application. Writer: tenant admins (dashboard, `twitchApps.set` /
 * `.remove`); the app access token by the Twitch module when it refreshes. `client_secret_ct` and
 * `app_access_token_ct` never reach any response: the dashboard shows whether a secret is set.
 * Replacing the app (a new client id) or removing it deletes the environment's
 * `twitch_identities`, whose tokens belong to the old app.
 */
export const twitchApps = pgTable("twitch_apps", {
  environmentId: text("environment_id")
    .primaryKey()
    .references(() => environments.id, { onDelete: "cascade" }),
  tenantId: text("tenant_id")
    .notNull()
    .references(() => tenant.id, { onDelete: "cascade" }),
  /** Public: from the app's page in the Twitch developer console. */
  clientId: text("client_id").notNull(),
  /** AES-256-GCM, key-id prefixed. Never tenant-facing. */
  clientSecretCt: text("client_secret_ct").notNull(),
  /** The client-credentials token for Helix `/users` and `/streams`, encrypted. Never tenant-facing. */
  appAccessTokenCt: text("app_access_token_ct"),
  appTokenExpiresAt: timestamp("app_token_expires_at", { withTimezone: true }),
  /** The last successful client-credentials grant; a save runs one, so a wrong secret is refused. */
  verifiedAt: timestamp("verified_at", { withTimezone: true }).notNull(),
  updatedByUserId: text("updated_by_user_id").references(() => user.id, { onDelete: "set null" }),
  createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
});

/**
 * One shopper's linked Twitch account, through their environment's app. Writer: the link, unlink
 * and validation flows (`core/src/twitch/`). `access_token_ct` and `refresh_token_ct` never leave
 * `packages/core`, and are left out of every read and of analytics; staff see the login alone.
 * One Twitch user links to one end user per environment; a re-link to another Twitch user replaces
 * the row and revokes the displaced token.
 */
export const twitchIdentities = pgTable(
  "twitch_identities",
  {
    id: text("id").primaryKey(), // ulid
    tenantId: text("tenant_id")
      .notNull()
      .references(() => tenant.id, { onDelete: "cascade" }),
    environmentId: text("environment_id")
      .notNull()
      .references(() => environments.id, { onDelete: "cascade" }),
    endUserId: text("end_user_id")
      .notNull()
      .references(() => endUsers.id, { onDelete: "cascade" }),
    twitchUserId: text("twitch_user_id").notNull(),
    twitchLogin: text("twitch_login").notNull(),
    twitchDisplayName: text("twitch_display_name"),
    /** AES-256-GCM, key-id prefixed. Never leaves core. */
    accessTokenCt: text("access_token_ct").notNull(),
    /** AES-256-GCM, key-id prefixed. Never leaves core. */
    refreshTokenCt: text("refresh_token_ct").notNull(),
    tokenExpiresAt: timestamp("token_expires_at", { withTimezone: true }).notNull(),
    /** `user:read:subscriptions`. */
    scopes: text("scopes").array().notNull(),
    /** Twitch asks apps to validate a user token hourly; a read validates when due. */
    validatedAt: timestamp("validated_at", { withTimezone: true }).notNull(),
    /** Set by a rejected refresh or validation: the shopper must reconnect. */
    revokedAt: timestamp("revoked_at", { withTimezone: true }),
    linkedAt: timestamp("linked_at", { withTimezone: true }).notNull(),
    updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    uniqueIndex("twitch_identities_end_user_idx").on(t.endUserId),
    uniqueIndex("twitch_identities_env_twitch_user_idx").on(t.environmentId, t.twitchUserId),
  ],
);

/**
 * A cache of public Twitch channel facts, platform-wide (no tenant data: any app's token reads the
 * same answer). Writer: the Twitch module, at most once a minute per login; the daily
 * `TwitchCachePurge` deletes rows not fetched for a day (boxed's one-day TTL), so a lookup of
 * logins nobody watches leaves nothing behind. Display only; never a live-break transition.
 */
export const twitchChannels = pgTable(
  "twitch_channels",
  {
    /** The Twitch login, lowercase. */
    login: text("login").primaryKey(),
    twitchUserId: text("twitch_user_id"),
    displayName: text("display_name"),
    avatarUrl: text("avatar_url"),
    /** Set only while the channel is live. */
    viewerCount: integer("viewer_count"),
    /** False = Twitch knows no such login. */
    isFound: boolean("is_found").notNull(),
    fetchedAt: timestamp("fetched_at", { withTimezone: true }).notNull(),
  },
  (t) => [index("twitch_channels_fetched_at_idx").on(t.fetchedAt)],
);

/**
 * A cache of whether one Twitch user subscribes to a broadcaster, platform-wide: five minutes when
 * subscribed, thirty seconds when not. Writer: the priority check; the daily `TwitchCachePurge`
 * deletes expired rows (boxed's TTL). Never tenant-facing.
 */
export const twitchSubscriptionChecks = pgTable(
  "twitch_subscription_checks",
  {
    buyerTwitchUserId: text("buyer_twitch_user_id").notNull(),
    broadcasterTwitchUserId: text("broadcaster_twitch_user_id").notNull(),
    isSubscribed: boolean("is_subscribed").notNull(),
    expiresAt: timestamp("expires_at", { withTimezone: true }).notNull(),
  },
  (t) => [
    primaryKey({ columns: [t.buyerTwitchUserId, t.broadcasterTwitchUserId] }),
    index("twitch_subscription_checks_expires_at_idx").on(t.expiresAt),
  ],
);

/**
 * A staff-listed Twitch streamer the storefront's widget shows: as the sponsored row while they
 * host a live break, as the promoted stream while they are live on Twitch without one, and by
 * name in the priority note. Writer: tenant staff (dashboard). `updated_by_user_id` is staff-only on
 * the wire.
 */
export const liveBreakSponsoredStreams = pgTable(
  "live_break_sponsored_streams",
  {
    id: text("id").primaryKey(), // ulid
    tenantId: text("tenant_id")
      .notNull()
      .references(() => tenant.id, { onDelete: "cascade" }),
    environmentId: text("environment_id")
      .notNull()
      .references(() => environments.id, { onDelete: "cascade" }),
    /** Lowercase Twitch login. */
    twitchLogin: text("twitch_login").notNull(),
    /** Display order. */
    priority: integer("priority").notNull(),
    /** Names the channel in the priority note. */
    displayName: text("display_name"),
    /** "Shown in Sponsored Streams" until then (1–72 h from a show), expired at read time. */
    shownUntil: timestamp("shown_until", { withTimezone: true }),
    updatedByUserId: text("updated_by_user_id").references(() => user.id, { onDelete: "set null" }),
    createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    uniqueIndex("live_break_sponsored_streams_env_login_idx").on(t.environmentId, t.twitchLogin),
    index("live_break_sponsored_streams_shown_idx")
      .on(t.environmentId, t.shownUntil)
      .where(sql`${t.shownUntil} IS NOT NULL`),
  ],
);
